import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { getProjects } from "@/lib/api";
import { PageHero } from "@/components/ui-kit";
import { useSeo } from "@/lib/useSeo";

const FILTERS = ["All", "Residential", "Kitchen", "Living", "Bedroom", "Commercial"];

export default function Gallery() {
  useSeo({ title: "Gallery", description: "A curated gallery of Sutra Interio's luxury interior projects across Kolkata." });
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const { data: projects = [] } = useQuery({ queryKey: ["projects", filter], queryFn: () => getProjects(filter) });

  return (
    <div data-testid="gallery-page">
      <PageHero overline="Portfolio" title="Spaces we&apos;ve" italic="brought to life."
        sub="Filter by project type to explore our craftsmanship across homes and workspaces in Kolkata." />

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-24">
        <div className="flex flex-wrap gap-3 mb-10">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`gallery-filter-${f.toLowerCase()}`} onClick={() => setFilter(f)}
              className={`text-sm uppercase tracking-[0.2em] font-bold px-5 py-2.5 rounded-full border transition-colors duration-300 ${
                filter === f ? "bg-olive text-beige border-ink" : "border-gold/40 text-ink/70 hover:border-bronze hover:text-bronze"
              }`}>
              {f}
            </button>
          ))}
        </div>

        <motion.div layout className="columns-1 sm:columns-2 lg:columns-3 gap-6" data-testid="gallery-grid">
          <AnimatePresence>
            {projects.map((p) => (
              <motion.div key={p.slug} layout initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                transition={{ duration: 0.5 }} className="break-inside-avoid mb-6">
                <button onClick={() => setActive(p)} data-testid={`gallery-item-${p.slug}`} className="group block w-full text-left overflow-hidden rounded-md relative">
                  <img src={p.image} alt={p.title} className="w-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/40 transition-colors flex items-end p-6">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity translate-y-2 group-hover:translate-y-0 duration-300">
                      <span className="overline !text-gold">{p.category}</span>
                      <p className="font-display text-2xl text-ivory">{p.title}</p>
                    </div>
                  </div>
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setActive(null)} data-testid="gallery-lightbox"
            className="fixed inset-0 z-[70] bg-ink/90 grid place-items-center p-4 md:p-10">
            <button className="absolute top-6 right-6 text-ivory" onClick={() => setActive(null)} aria-label="Close"><X size={28} /></button>
            <motion.div initial={{ scale: 0.94 }} animate={{ scale: 1 }} className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
              <img src={active.image} alt={active.title} className="w-full max-h-[75vh] object-contain rounded-md" />
              <div className="mt-4 text-ivory">
                <span className="overline !text-gold">{active.category} · {active.location}</span>
                <h3 className="font-display text-3xl">{active.title}</h3>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
